import React, { useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import {
  Empty, Input, Table, Tag, Typography,
} from 'antd';
import {
  sortSkusByContainerType,
  stripProductSkuShortLabel,
} from '../../../../util/sku-formatter';

const { Text } = Typography;

function renderCopyableReadOnly(value) {
  if (value == null || String(value).trim() === '') {
    return <Text type="secondary">—</Text>;
  }
  const s = String(value);
  return <Text copyable>{s}</Text>;
}

const BARCODE_COLUMN_META = {
  bnUnit: { title: 'Barcode Unit', ariaLabel: 'Barcode unit', width: '16%' },
  bnPack: { title: 'Barcode Pack', ariaLabel: 'Barcode pack', width: '16%' },
  bnCase: { title: 'Barcode Case', ariaLabel: 'Barcode case', width: '16%' },
};

const BARCODE_FIELDS = ['bnUnit', 'bnPack', 'bnCase'];

const FOODSTUFFS_COLUMN_META = {
  foodstuffsArticleNi: {
    title: 'Foodstuffs NI Article',
    ariaLabel: 'Foodstuffs NI SAP article number',
    width: '16%',
  },
  foodstuffsArticleSi: {
    title: 'Foodstuffs SI Article',
    ariaLabel: 'Foodstuffs SI SAP article number',
    width: '16%',
  },
};

const FOODSTUFFS_FIELDS = ['foodstuffsArticleNi', 'foodstuffsArticleSi'];

const DATA_COLUMN_META = { ...BARCODE_COLUMN_META, ...FOODSTUFFS_COLUMN_META };
const DATA_FIELD_ORDER = [...BARCODE_FIELDS, ...FOODSTUFFS_FIELDS];

const BarcodesSummary = ({
  skus,
  productName,
  canEditSku,
  onSkuDataFieldChange,
}) => {
  const orderedSkus = useMemo(
    () => (skus?.length ? sortSkusByContainerType(skus) : []),
    [skus],
  );

  const stripProductName = useCallback(
    (name) => stripProductSkuShortLabel(name, productName || ''),
    [productName],
  );

  const renderDataCell = useCallback(
    (record, fieldName) => {
      const raw = record[fieldName];
      const value = raw == null ? '' : String(raw);

      if (!canEditSku) {
        return renderCopyableReadOnly(raw);
      }

      const meta = DATA_COLUMN_META[fieldName];

      return (
        <Input
          size="small"
          style={{ width: '100%', minWidth: 0, boxSizing: 'border-box' }}
          aria-label={meta.ariaLabel}
          value={value}
          onChange={(e) => {
            const next = e.target.value;
            if (record.key == null) return;
            onSkuDataFieldChange(record.key, fieldName, next);
          }}
          allowClear
        />
      );
    },
    [canEditSku, onSkuDataFieldChange],
  );

  const columns = useMemo(() => [
    {
      title: 'SKU',
      dataIndex: 'name',
      key: 'name',
      width: '20%',
      render: (name, record) => (
        <div>
          <Text strong>{stripProductName(name) || 'Unnamed SKU'}</Text>
          {record.isVenueSku && (
            <Tag color="purple" style={{ marginLeft: 8 }}>Venue</Tag>
          )}
          {record.isLegacy && (
            <Tag color="default" style={{ marginLeft: 6, fontSize: 10 }}>Legacy</Tag>
          )}
        </div>
      ),
    },
    ...DATA_FIELD_ORDER.map((fieldName) => {
      const meta = DATA_COLUMN_META[fieldName];
      return {
        title: meta.title,
        key: fieldName,
        width: meta.width,
        render: (_, record) => renderDataCell(record, fieldName),
      };
    }),
  ], [stripProductName, renderDataCell]);

  if (!skus || skus.length === 0) {
    return (
      <Empty
        image={Empty.PRESENTED_IMAGE_SIMPLE}
        description="No SKUs yet — add SKUs in the SKUs tab."
      />
    );
  }

  return (
    <Table
      className="sku-summary-table product-editor-data-summary-table"
      dataSource={orderedSkus}
      columns={columns}
      rowKey={(row, index) => (
        row.key != null ? String(row.key) : `row-${index}-${row.name || ''}`
      )}
      pagination={false}
      size="small"
      bordered
    />
  );
};

BarcodesSummary.propTypes = {
  productName: PropTypes.string,
  skus: PropTypes.arrayOf(PropTypes.object),
  canEditSku: PropTypes.bool,
  onSkuDataFieldChange: PropTypes.func,
};

BarcodesSummary.defaultProps = {
  productName: null,
  skus: [],
  canEditSku: false,
  onSkuDataFieldChange: () => {},
};

export default BarcodesSummary;
